/**
 * CompletenessCard — Shows scrape completeness stats in ScraperDashboard
 * Fetches from /api/completeness/summary
 * Shows: total saved, total expected, overall %, per-dept breakdown (expandable)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, AlertTriangle, HelpCircle, ChevronDown, ChevronRight, RefreshCw, BarChart2 } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './CompletenessCard.css';

const PCT_COLOR = (pct) => {
  if (pct === null || pct === undefined) return '#64748b';
  if (pct >= 95) return '#10b981';
  if (pct >= 70) return '#f59e0b';
  return '#ef4444';
};

const PCT_LABEL = (pct) => {
  if (pct === null || pct === undefined) return 'Unknown';
  if (pct >= 95) return 'Complete';
  if (pct >= 70) return 'Partial';
  return 'Incomplete';
};

function MiniBar({ pct, saved, expected }) {
  const filled = Math.min(pct ?? 0, 100);
  return (
    <div className="cc-bar-wrap" title={expected ? `${saved?.toLocaleString()} / ${expected?.toLocaleString()} POs` : `${saved?.toLocaleString()} POs`}>
      <div className="cc-bar-track">
        <motion.div
          className="cc-bar-fill"
          style={{ background: PCT_COLOR(pct) }}
          initial={{ width: 0 }}
          animate={{ width: `${filled}%` }}
          transition={{ type: 'spring', stiffness: 120, damping: 20, delay: 0.1 }}
        />
      </div>
      <span className="cc-bar-pct" style={{ color: PCT_COLOR(pct) }}>
        {pct != null ? `${pct.toFixed(0)}%` : '?'}
      </span>
    </div>
  );
}

export default function CompletenessCard({ className }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [deptExpanded, setDeptExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/completeness/summary');
      setData(res);
    } catch (e) {
      console.warn('CompletenessCard fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className={`cc-card ${className || ''}`}>
      <div className="cc-loading"><RefreshCw size={16} className="cc-spin" /> Loading completeness...</div>
    </div>
  );

  const s = data?.summary;
  const depts = data?.by_dept || [];
  const pct = s?.overall_completeness_pct ? parseFloat(s.overall_completeness_pct) : null;
  const totalSaved = parseInt(s?.total_saved || 0);
  const totalExpected = parseInt(s?.total_expected || 0);
  const completeMonths = parseInt(s?.complete_months || 0);
  const partialMonths = parseInt(s?.partial_months || 0);
  const unknownMonths = parseInt(s?.unknown_months || 0);

  return (
    <div className={`cc-card ${className || ''}`}>
      {/* Header */}
      <div className="cc-header" onClick={() => setExpanded(e => !e)}>
        <div className="cc-header-left">
          <BarChart2 size={15} className="cc-icon" />
          <span className="cc-title">Scrape Completeness</span>
          {pct != null && (
            <span className="cc-badge" style={{ background: `${PCT_COLOR(pct)}22`, color: PCT_COLOR(pct), borderColor: `${PCT_COLOR(pct)}44` }}>
              {pct.toFixed(0)}% overall
            </span>
          )}
        </div>
        <div className="cc-header-right">
          <button className="cc-refresh" onClick={(e) => { e.stopPropagation(); load(); }} title="Refresh">
            <RefreshCw size={12} />
          </button>
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="cc-body"
          >
            {/* Big stat row */}
            <div className="cc-stats">
              <div className="cc-stat">
                <span className="cc-stat-val">{totalSaved.toLocaleString()}</span>
                <span className="cc-stat-label">POs saved</span>
              </div>
              {totalExpected > 0 && (
                <div className="cc-stat">
                  <span className="cc-stat-val">{totalExpected.toLocaleString()}</span>
                  <span className="cc-stat-label">POs expected</span>
                </div>
              )}
              <div className="cc-stat cc-stat--green">
                <span className="cc-stat-val">{completeMonths}</span>
                <span className="cc-stat-label">≥95% months</span>
              </div>
              <div className="cc-stat cc-stat--amber">
                <span className="cc-stat-val">{partialMonths}</span>
                <span className="cc-stat-label">partial months</span>
              </div>
              <div className="cc-stat cc-stat--muted">
                <span className="cc-stat-val">{unknownMonths}</span>
                <span className="cc-stat-label">no count</span>
              </div>
            </div>

            {/* Overall bar */}
            {pct != null && (
              <div className="cc-overall-bar">
                <span className="cc-overall-label">Overall completeness</span>
                <MiniBar pct={pct} saved={totalSaved} expected={totalExpected} />
              </div>
            )}

            {/* Legend */}
            <div className="cc-legend">
              <span className="cc-legend-item cc-legend-complete"><CheckCircle size={10} /> ≥95% complete</span>
              <span className="cc-legend-item cc-legend-partial"><AlertTriangle size={10} /> 70–94% partial</span>
              <span className="cc-legend-item cc-legend-unknown"><HelpCircle size={10} /> Unknown (no count)</span>
            </div>

            {/* Per-dept breakdown */}
            {depts.length > 0 && (
              <div className="cc-dept-section">
                <button className="cc-dept-toggle" onClick={() => setDeptExpanded(e => !e)}>
                  {deptExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  {depts.length} departments with completeness data
                </button>
                <AnimatePresence>
                  {deptExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                      className="cc-dept-list"
                    >
                      {depts.map(d => (
                        <div key={d.dept_code} className="cc-dept-row">
                          <span className="cc-dept-code">{d.dept_code}</span>
                          <span className="cc-dept-months">{d.months_scraped}mo</span>
                          <div className="cc-dept-bar">
                            <MiniBar pct={d.avg_completeness_pct ? parseFloat(d.avg_completeness_pct) : null} saved={d.total_saved} expected={d.total_expected} />
                          </div>
                          <span className="cc-dept-saved">{parseInt(d.total_saved || 0).toLocaleString()} POs</span>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
